import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { useCrumbItemsLoading } from 'hooks';
import { insertDivider } from 'utils/element';
import {
  getLabelByRouteName,
  getSystemCondition,
  needAddPrefixRoutes,
} from 'apps/routes/utils';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import {
  FrameHeaderHeight,
  FrameSidebarWidth,
  SubHeaderHeight,
} from 'styles/constants';
import { themes } from 'styles/themes/wikifactoryNew';

import { routerStore } from 'core/routing';
import { isProfileSlug } from 'utils';
import { clientStore } from 'apps/client/app';
import Revisions from './Revisions';
import { HistoryNavigation } from './HistoryNavigation';
import { BackButton } from './BackButton';
import { SubHeaderActions } from './SubHeaderActions';
import { SubHeaderBreadcrumbs } from './SubHeaderBreadcrumbs';

type SubHeaderProps = {
  lefts?: ReactNode[];
  rights?: ReactNode[];
  className?: string;
};

export const SubHeader = observer(
  ({ lefts, rights, className = '' }: SubHeaderProps) => {
    const { shouldShowFrameSideBar, shouldShowCADRevisions } =
      getSystemCondition();
    const { route } = routerStore;
    const { slugFromRouteOrSpace, activeInitiativeSpace } = clientStore;

    const { crumbItemsLoading } = useCrumbItemsLoading();

    const isProfileSpace = isProfileSlug(slugFromRouteOrSpace);
    const isInitiativeSpace = !isProfileSpace;
    const { title: initiativeName = '' } = activeInitiativeSpace?.content || {};
    const pageName = getLabelByRouteName(route.name);

    const prefixedPageName = isInitiativeSpace
      ? `${initiativeName}${initiativeName ? "'s" : ''} ${pageName}`
      : `${needAddPrefixRoutes.includes(route.name) ? 'My ' : ''}${pageName}`;

    const subheaderMainContent = shouldShowFrameSideBar ? (
      <h6 key='activeSpacePrefix'>{prefixedPageName}</h6>
    ) : (
      <SubHeaderBreadcrumbs />
    );

    const leftContent = [
      ...insertDivider([
        subheaderMainContent,
        shouldShowCADRevisions && !crumbItemsLoading && (
          <Revisions key='Revisions' />
        ),
        ...(lefts || []),
      ]),
    ].filter(Boolean);

    const sideBarWidth = shouldShowFrameSideBar ? FrameSidebarWidth : 0;

    return (
      <div
        className={`flex items-center h-[${SubHeaderHeight}px] w-full fixed top-[${FrameHeaderHeight}px] z-[9999] bg-white`}
        style={{
          borderBottom: `1px solid ${themes.color.wfGrey100}`,
          width: `calc(100% - ${sideBarWidth}px)`, // TODO: remember when side bard shrink, it should be recalc
        }}
      >
        <div
          data-testid='SubHeader'
          className={`${
            shouldShowFrameSideBar ? 'px-4' : ''
          } text-black w-full flex-shrink-0 h-[${SubHeaderHeight}px] top-0 flex justify-between items-center ${className}`}
        >
          <div
            data-testid='subheader-leftSection'
            className='flex items-center select-none w-fit'
          >
            {!shouldShowFrameSideBar && <BackButton />}
            {isInDesktopMode() && !shouldShowFrameSideBar && (
              <HistoryNavigation />
            )}
            {leftContent}
          </div>

          <SubHeaderActions rights={rights} />
        </div>
      </div>
    );
  },
);
